import IconButton from '@components/v2/ui/IconButton/IconButton';
import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Grid, Paper, Theme } from '@mui/material';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import LangEnum from '../../lang/LangEnum';
import { SignatureInfo } from '../../lib/SiteLib';
import { useTranslation } from '../../modules/translation/TranslationContext';
import { ShowroomTestIds } from '../../utils/testIds/Showroom.testIds';
import LoadingCards from './LoadingCards';
import PersonSignature from './PersonSignature';
import PersonSignatureForm from './PersonSignatureForm';
import Section, { SectionProps } from './Section';

const useStyles = makeStyles()((theme: Theme) => ({
  signatureCard: {
    position: 'relative',
  },
  editAction: {
    position: 'absolute',
    top: theme.spacing(-2),
    right: theme.spacing(2),
  },
}));

type PersonSignatureSectionProps<T> = {
  signatures: Array<T>;
  sectionProps: Partial<SectionProps>;
  title: string;
  onSubmit: (values: SignatureInfo) => Promise<boolean | undefined>;
};

const PersonSignatureSection = <T extends SignatureInfo>({
  signatures,
  sectionProps,
  title,
  onSubmit,
}: PersonSignatureSectionProps<T>) => {
  const { classes } = useStyles();
  const [editingSignature, setEditingSignature] = useState<T | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const { translate } = useTranslation();

  const handleSubmit = async (values: SignatureInfo) => {
    setLoading(true);
    const success = await onSubmit(values);
    if (success) {
      setEditingSignature(undefined);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <Section {...sectionProps}>
        <LoadingCards aspectRatio={0.5} />
      </Section>
    );
  }

  return (
    <>
      <Modal
        isOpen={!!editingSignature}
        size="standard"
        header={{ title: translate(LangEnum.SHOWROOM_PERSON_SIGNATURE_TITLE) }}
        isDismissable={false}
        onClose={() => setEditingSignature(undefined)}
      >
        <PersonSignatureForm
          loading={loading}
          initialValues={editingSignature}
          onCancel={() => setEditingSignature(undefined)}
          onSave={handleSubmit}
        />
      </Modal>
      <Section {...sectionProps} readOnly>
        <Box
          test-id={ShowroomTestIds.FEATURED_CONTACTS_DETAILS}
          sx={{
            pb: 2,
          }}
        >
          <Typography variant="h5" color="text.primary">
            {title}
          </Typography>
        </Box>
        <Grid container spacing={4} test-id={ShowroomTestIds.FEATURED_CONTACTS_GRID_DETAILS}>
          {signatures.map((signature, index) => (
            <Grid
              key={`${signature.email}-${index}`}
              size={{
                xs: 12,
                sm: 6,
                lg: 4,
              }}
            >
              <Paper variant="outlined" className={classes.signatureCard}>
                {!sectionProps.readOnly && signature.editable && (
                  <IconButton
                    icon={Pencil}
                    color="secondary"
                    shape="round"
                    className={classes.editAction}
                    ariaLabel={translate(LangEnum.GLOBAL_EDIT)}
                    onClick={() => setEditingSignature(signature)}
                  />
                )}
                <Box
                  test-id={ShowroomTestIds.FEATURED_CONTACTS_BOX_DETAILS}
                  sx={{
                    p: 2,
                  }}
                >
                  <PersonSignature {...signature} onEdit={() => setEditingSignature(signature)} />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Section>
    </>
  );
};

export default PersonSignatureSection;
